import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { listBooks, signInURL } from "../api";
import BookTile, { BookGrid } from "../components/BookTile";
import DeviceShot from "../components/DeviceShot";
import FeatureClips from "../components/FeatureClips";
import Placeholder, { Pending } from "../components/Placeholder";
import { useT } from "../i18n";
import LegalLinks from "../components/LegalLinks";
import { Lockup } from "../components/Logo";
import StoreButtons from "../components/StoreButtons";

/** The whole shelf in one page: the catalogue is a few dozen books (ADR-0197). */
const SHELF = 100;
/** Placeholders while it loads. */
const SAMPLES = 4;

/**
 * The screenshots are the app on the S9+ in its Vintage look, one by day and
 * one by night, made by scripts/site-shots (capture.sh drives the phone,
 * build.sh crops and converts), so they can be made again when the app
 * changes rather than going stale (requirement 0079).
 */
const SHOTS = "/landing/site";
// One per entry of t.landing.features, in order.
const FEATURE_SHOTS = ["ask", "book", "picture", "library"];

/** The faces in the cast band: public books' portraits, in the order shown. */
const FACES = [
  "hamlet-hamlet",
  "hamlet-ophelia",
  "hamlet-claudius",
  "alice-alice",
  "alice-cheshire-cat",
  "driftwood-king-achaemenes",
  "driftwood-king-katayoun",
  "moby-dick-ishmael",
  "odyssey-queen-penelope",
  "hamlet-gertrude",
  "moby-dick-starbuck",
  "odyssey-telemachus",
];

/**
 * Landing is what somebody without an account sees at `/` (ADR-0070), drawn
 * in the app's Vintage look — the reading room, brass and parchment (owner's
 * pick, 2026-10-22) — in the site's light or dark.
 *
 * The shelf is every public book a visitor may see the face of — the same
 * listing `/browse` shows them (ADR-0197) — each badged by the server's
 * `access` and saying what it holds, so the books behind Plus are lures rather
 * than secrets. "More" opens Browse, with its tags and collections.
 *
 * The main button opens the first book that opens now, because reading one is
 * something a visitor can do without an account and signing in is not a
 * reason to stay (requirement 0079). The header's sign-in is then the only
 * one; this page offers its own only when nothing is open, where it is the
 * only way in.
 */
export default function Landing() {
  const t = useT();
  const { data, isPending } = useQuery({
    queryKey: ["books", "public", "shelf"],
    queryFn: () => listBooks(undefined, undefined, SHELF),
  });
  const books = data?.items ?? [];
  // The first that opens now, in the server's order; a server from before
  // `access` (ADR-0197) listed only open books.
  const first = books.find((b) => (b.access ?? "open") === "open");

  const start = first ? (
    <>
      <Link to={`/books/${first.id}`} data-testid="landing-start" className="vintage-button">
        {t.landing.start} <bdi className="ms-1">{first.title}</bdi>
      </Link>
      <p className="mt-3 text-sm opacity-80">{t.landing.startNote}</p>
    </>
  ) : (
    !isPending && (
      <a href={signInURL("/")} data-testid="landing-sign-in" className="vintage-button">
        {t.landing.signIn}
      </a>
    )
  );

  return (
    <main className="vintage">
      <section className="vintage-hero" data-testid="landing-hero">
        <img src={`${SHOTS}/plate-day.webp`} alt="" className="vintage-plate dark:hidden" />
        <img src={`${SHOTS}/plate-night.webp`} alt="" className="vintage-plate hidden dark:block" />
        <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-10 px-5 pt-[min(62vw,20rem)] pb-14 text-center md:grid-cols-[1.15fr_1fr] md:pt-24 md:pb-20 md:text-start">
          <div className="text-[#f6ead2]">
            <h1 className="vintage-serif text-[2.5rem] leading-[1.06] font-bold text-balance sm:text-6xl">
              {t.landing.title}
            </h1>
            <Ornament className="mx-auto my-5 md:mx-0" />
            <p className="mx-auto max-w-xl text-[1.05rem] leading-7 text-[#ead9bb] md:mx-0 md:text-lg">
              {t.landing.blurb}
            </p>
            <div className="mt-8 min-h-[4.5rem]">{start}</div>
            <StoreButtons className="mt-6 justify-center md:justify-start" />
          </div>
          <DeviceShot
            day={`${SHOTS}/listen-day.webp`}
            night={`${SHOTS}/listen-night.webp`}
            alt={t.landing.heroAlt}
            eager
            className="mx-auto w-[15.5rem] rotate-[3deg] sm:w-[18rem]"
          />
        </div>
      </section>

      <FeatureClips />

      <section className="px-5 pt-12 pb-4 text-center" data-testid="landing-faces">
        <h2 className="vintage-serif text-2xl font-bold">{t.landing.facesTitle}</h2>
        <div className="mt-5 flex flex-wrap justify-center">
          {FACES.map((face) => (
            <img
              key={face}
              src={`${SHOTS}/faces/${face}.webp`}
              alt=""
              loading="lazy"
              width={192}
              height={192}
              className="vintage-face"
            />
          ))}
        </div>
        <p className="mt-4 text-[0.95rem] text-[var(--v-soft)]">{t.landing.facesBody}</p>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-6" data-testid="landing-features">
        {t.landing.features.map((feature, i) => (
          <div
            key={feature.title}
            data-testid="landing-feature"
            className="grid items-center gap-8 border-b border-[var(--v-line)] py-12 text-center last:border-0 md:grid-cols-[1fr_auto] md:gap-16 md:py-16 md:text-start"
          >
            <div className={i % 2 === 1 ? "md:order-2" : undefined}>
              <span className="vintage-serif text-sm tracking-[0.2em] text-[var(--v-brass)]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h2 className="vintage-serif mt-1 text-[1.8rem] leading-tight font-bold sm:text-4xl">
                {feature.title}
              </h2>
              <p className="mx-auto mt-3 max-w-md text-[1.05rem] leading-7 text-[var(--v-soft)] md:mx-0">
                {feature.body}
              </p>
            </div>
            <DeviceShot
              day={`${SHOTS}/${FEATURE_SHOTS[i]}-day.webp`}
              night={`${SHOTS}/${FEATURE_SHOTS[i]}-night.webp`}
              alt={feature.alt}
              className="mx-auto w-[15rem] sm:w-[16.5rem]"
            />
          </div>
        ))}
      </section>

      {/* Absent, not present and dead, when nothing is open (ADR-0070). A
          heading over an empty shelf and a "more" leading to another empty one
          reads as a broken page; without them this still says what SageLeaf is
          and offers a way in, which is the job. */}
      {(isPending || books.length > 0) && (
        <section className="vintage-shelf px-5 py-14 sm:py-20" data-testid="landing-samples">
          <div className="mx-auto max-w-5xl">
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <h2 className="vintage-serif text-2xl font-bold sm:text-3xl">
                {t.landing.shelfTitle}
              </h2>
              <Link
                to="/browse"
                data-testid="landing-more"
                className="rounded-control px-2 py-1 text-sm font-medium text-[var(--v-brass)] underline-offset-4 hover:underline"
              >
                {t.landing.more}
              </Link>
            </div>
            <p className="mb-7 text-[0.95rem] text-[var(--v-soft)]">{t.landing.legend}</p>

            {isPending ? (
              <Pending label={t.common.loading}>
                <BookGrid>
                  {Array.from({ length: SAMPLES }, (_, i) => (
                    <li key={i}>
                      <Placeholder className="aspect-[3/4] rounded-card border border-line" />
                      <Placeholder className="mt-2 h-3 w-3/4 rounded-full" />
                    </li>
                  ))}
                </BookGrid>
              </Pending>
            ) : (
              <BookGrid testID="landing-book-list">
                {books.map((book) => (
                  <BookTile key={book.id} book={book} face />
                ))}
              </BookGrid>
            )}
          </div>
        </section>
      )}

      <footer
        className="flex flex-col items-center gap-3 border-t border-[var(--v-line)] px-5 py-6"
        data-testid="landing-footer"
      >
        <Lockup size="sm" />
        <LegalLinks about />
      </footer>
    </main>
  );
}

/** The brass rule under a heading: two hairlines and a lozenge, as in the app's Vintage headers. */
function Ornament({ className = "" }: { className?: string }) {
  return (
    <div className={`vintage-ornament ${className}`} aria-hidden="true">
      <i />
      <b />
      <i />
    </div>
  );
}
